import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { adminApiAxios } from '@/api'
import { fetchWrapper } from '@/reducers/fetch'
import type {
  AdminBankingProfile,
  GetAdminBankingProfileResponse,
} from '@/features/banking/banking.types'
import type { ReduxState } from '@/utils/typeHelpers'

export type AdminBankingProfileState = {
  byUserId: Record<number, AdminBankingProfile | null>
}

const initialState: AdminBankingProfileState = { byUserId: {} }

const adminBankingProfileSlice = createSlice({
  name: 'adminBankingProfile',
  initialState,
  reducers: {
    receiveAdminBankingProfile: (
      state,
      action: PayloadAction<{
        userId: number
        profile: AdminBankingProfile | null
      }>
    ) => {
      state.byUserId[action.payload.userId] = action.payload.profile
    },
    clearAdminBankingProfile: (state, action: PayloadAction<number>) => {
      delete state.byUserId[action.payload]
    },
  },
})

export default adminBankingProfileSlice.reducer
export const { receiveAdminBankingProfile, clearAdminBankingProfile } =
  adminBankingProfileSlice.actions

export const FETCH_ADMIN_BANKING_PROFILE_KEY = 'FETCH_ADMIN_BANKING_PROFILE_KEY'

export const fetchAdminBankingProfile = (userId: number) =>
  fetchWrapper({
    fetchKey: `${FETCH_ADMIN_BANKING_PROFILE_KEY}_${userId}`,
    fetchFunction: async (dispatch) => {
      try {
        const res = await adminApiAxios.get<GetAdminBankingProfileResponse>(
          `/finances/api/v1/admin/users/${userId}/banking-profile`
        )
        dispatch(
          receiveAdminBankingProfile({
            userId,
            profile: res.data.bankingProfile,
          })
        )
        return res.data.bankingProfile
      } catch (err) {
        // Set null so the panel falls through to "No banking profile" instead
        // of getting stuck on the loader; fetchWrapper still surfaces the
        // error through its existing pipeline.
        dispatch(receiveAdminBankingProfile({ userId, profile: null }))
        throw err
      }
    },
  })

export const selectAdminBankingProfile = (
  state: ReduxState,
  userId: number
): AdminBankingProfile | null | undefined =>
  state.adminBankingProfile.byUserId[userId]
